<script lang="ts">
	import { _ } from 'svelte-i18n';
	import { onMount } from 'svelte';
	import Icon from '$lib/components/primitives/Icon.svelte';

	let scrollY = $state(0);
	let innerHeight = $state(0);

	onMount(() => {
		const handleScroll = () => (scrollY = window.scrollY);
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	});
</script>

<svelte:window bind:scrollY bind:innerHeight />

<section class="story-section">
	<div class="container">
		<div class="story-content">
			<div class="chapter">
				<h2 class="chapter-title">{$_('story.chapter1.title')}</h2>
				<div class="narrative">
					<p class="lead">{$_('story.chapter1.lead')}</p>
					<p>{$_('story.chapter1.p1')}</p>
					<p>{$_('story.chapter1.p2')}</p>
				</div>

				<div class="feature-spotlight" style="--offset: {scrollY * 0.1}px">
					<div class="spotlight-content">
						<h3>{$_('story.chapter1.spotlight.title')}</h3>
						<p>{$_('story.chapter1.spotlight.description')}</p>
						<div class="spotlight-visual">
							<div class="pulse-screenshot-frame">
								<div class="pulse-screenshot-chrome">
									<span class="chrome-dot"></span>
									<span class="chrome-dot"></span>
									<span class="chrome-dot"></span>
									<span class="chrome-label">Pulse</span>
								</div>
								<img
									src="/images/pulse-screenshot.png"
									alt="Heyweek Pulse feed — real-time team updates"
									class="pulse-screenshot-img"
									width="960"
									height="710"
									loading="lazy"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div class="chapter alternate">
				<h2 class="chapter-title">{$_('story.chapter2.title')}</h2>
				<div class="narrative">
					<p class="lead">{$_('story.chapter2.lead')}</p>
					<p>{$_('story.chapter2.p1')}</p>
					<p>{$_('story.chapter2.p2')}</p>
				</div>

				<div class="inline-features">
					<div class="inline-feature">
						<div class="feature-icon">
							<Icon name="credit-card" size={32} />
						</div>
						<div class="feature-text">
							<h4>{$_('story.chapter2.feature1.title')}</h4>
							<p>{$_('story.chapter2.feature1.description')}</p>
						</div>
					</div>
					<div class="inline-feature">
						<div class="feature-icon">
							<Icon name="trending-up" size={32} />
						</div>
						<div class="feature-text">
							<h4>{$_('story.chapter2.feature2.title')}</h4>
							<p>{$_('story.chapter2.feature2.description')}</p>
						</div>
					</div>
					<div class="inline-feature">
						<div class="feature-icon">
							<Icon name="refresh-cw" size={32} />
						</div>
						<div class="feature-text">
							<h4>{$_('story.chapter2.feature3.title')}</h4>
							<p>{$_('story.chapter2.feature3.description')}</p>
						</div>
					</div>
				</div>
			</div>

			<div class="chapter">
				<h2 class="chapter-title">{$_('story.chapter3.title')}</h2>
				<div class="narrative">
					<p class="lead">{$_('story.chapter3.lead')}</p>
					<p>{$_('story.chapter3.p1')}</p>
					<p>{$_('story.chapter3.p2')}</p>
				</div>

				<div class="comparison">
					<div class="before">
						<h4>{$_('story.chapter3.before.title')}</h4>
						<ul>
							<li>
								<Icon name="x" size={16} class="icon-before" />
								{$_('story.chapter3.before.item1')}
							</li>
							<li>
								<Icon name="x" size={16} class="icon-before" />
								{$_('story.chapter3.before.item2')}
							</li>
							<li>
								<Icon name="x" size={16} class="icon-before" />
								{$_('story.chapter3.before.item3')}
							</li>
							<li>
								<Icon name="x" size={16} class="icon-before" />
								{$_('story.chapter3.before.item4')}
							</li>
						</ul>
					</div>
					<div class="after">
						<h4>{$_('story.chapter3.after.title')}</h4>
						<ul>
							<li>
								<Icon name="check" size={16} class="icon-after" />
								{$_('story.chapter3.after.item1')}
							</li>
							<li>
								<Icon name="check" size={16} class="icon-after" />
								{$_('story.chapter3.after.item2')}
							</li>
							<li>
								<Icon name="check" size={16} class="icon-after" />
								{$_('story.chapter3.after.item3')}
							</li>
							<li>
								<Icon name="check" size={16} class="icon-after" />
								{$_('story.chapter3.after.item4')}
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>
	</div>
</section>

<style>
	.story-section {
		padding-block: calc(var(--section-padding-y) * 1.5);
		background: var(--bg-primary);
	}

	.story-content {
		max-width: 900px;
		margin-inline: auto;
	}

	.chapter {
		margin-bottom: var(--space-16);
		position: relative;
	}

	.chapter-title {
		font-size: clamp(2rem, 4vw, 3rem);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-8);
		line-height: 1.2;
	}

	.narrative {
		margin-bottom: var(--space-12);
	}

	.narrative p {
		font-size: var(--font-size-lg);
		line-height: 1.8;
		color: var(--text-secondary);
		margin-bottom: var(--space-6);
	}

	.narrative .lead {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-medium);
		color: var(--text-primary);
		line-height: 1.6;
		margin-bottom: var(--space-8);
	}

	.feature-spotlight {
		background: linear-gradient(
			135deg,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.05) 0%,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.02) 100%
		);
		border-radius: var(--radius-xl);
		padding: var(--space-12);
		margin-block: var(--space-12);
		transform: translateY(var(--offset, 0));
		transition: transform 0.3s ease-out;
	}

	.spotlight-content h3 {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		margin-bottom: var(--space-4);
		color: var(--text-primary);
	}

	.spotlight-content p {
		font-size: var(--font-size-lg);
		color: var(--text-secondary);
		margin-bottom: var(--space-8);
		line-height: 1.6;
	}

	.pulse-screenshot-frame {
		border-radius: var(--radius-lg);
		overflow: hidden;
		border: 1px solid var(--border-light);
		box-shadow: 0 8px 32px hsla(0, 0%, 0%, 0.18);
	}

	.pulse-screenshot-chrome {
		display: flex;
		align-items: center;
		gap: 6px;
		padding: 8px 14px;
		background: var(--bg-secondary);
		border-bottom: 1px solid var(--border-light);
	}

	.chrome-dot {
		width: 10px;
		height: 10px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.chrome-dot:nth-child(1) {
		background: hsl(0 75% 58%);
	}
	.chrome-dot:nth-child(2) {
		background: hsl(38 82% 56%);
	}
	.chrome-dot:nth-child(3) {
		background: hsl(130 52% 46%);
	}

	.chrome-label {
		margin-left: var(--space-2);
		font-size: var(--font-size-xs);
		color: var(--text-secondary);
		font-weight: var(--font-weight-medium);
	}

	.pulse-screenshot-img {
		display: block;
		width: 100%;
		height: auto;
	}

	.inline-features {
		display: flex;
		flex-direction: column;
		gap: var(--space-8);
		margin-block: var(--space-12);
	}

	.inline-feature {
		display: flex;
		gap: var(--space-6);
		align-items: flex-start;
	}

	.feature-icon {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 64px;
		height: 64px;
		background: linear-gradient(
			135deg,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.1) 0%,
			hsl(var(--brand-h) var(--brand-s) var(--brand-l) / 0.05) 100%
		);
		border-radius: var(--radius-lg);
		color: var(--text-brand);
		flex-shrink: 0;
	}

	.feature-text h4 {
		font-size: var(--font-size-xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-2);
	}

	.feature-text p {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
		line-height: 1.6;
	}

	.comparison {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: var(--space-8);
		margin-block: var(--space-12);
	}

	.before,
	.after {
		padding: var(--space-8);
		border-radius: var(--radius-lg);
	}

	.before {
		background: hsl(0 50% 50% / 0.05);
		border: 2px solid hsl(0 50% 50% / 0.1);
	}

	.after {
		background: hsl(120 50% 50% / 0.05);
		border: 2px solid hsl(120 50% 50% / 0.1);
	}

	.before h4,
	.after h4 {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		margin-bottom: var(--space-4);
	}

	.before h4 {
		color: var(--text-primary);
	}

	.after h4 {
		color: var(--text-primary);
	}

	.before ul,
	.after ul {
		list-style: none;
		padding: 0;
	}

	.before li,
	.after li {
		padding: var(--space-3) 0;
		display: flex;
		align-items: flex-start;
		gap: var(--space-3);
		color: var(--text-secondary);
		line-height: 1.5;
	}

	:global(.icon-before) {
		color: hsl(0 50% 50%);
		flex-shrink: 0;
		margin-top: 2px;
	}

	:global(.icon-after) {
		color: hsl(120 50% 45%);
		flex-shrink: 0;
		margin-top: 2px;
	}

	:global(.inline-icon) {
		display: inline;
		vertical-align: text-bottom;
		color: var(--text-secondary);
	}

	/* Mobile optimizations */
	@media (max-width: 768px) {
		.story-content {
			padding-inline: var(--space-4);
		}

		.chapter {
			margin-bottom: var(--space-16);
		}

		.chapter-title {
			font-size: 1.75rem;
			line-height: 1.3;
		}

		.narrative p {
			font-size: var(--font-size-base);
			line-height: 1.7;
		}

		.narrative .lead {
			font-size: var(--font-size-lg);
		}

		.comparison {
			grid-template-columns: 1fr;
			gap: var(--space-4);
		}

		.inline-feature {
			flex-direction: row;
			text-align: left;
			gap: var(--space-4);
		}

		.feature-icon {
			width: 48px;
			height: 48px;
		}

		.feature-spotlight {
			padding: var(--space-6);
			margin-block: var(--space-8);
		}

		.pulse-feed {
			padding: var(--space-4);
		}

		.pulse-post {
			padding: var(--space-3);
			font-size: var(--font-size-sm);
		}

		.before,
		.after {
			padding: var(--space-6);
		}

		.before li,
		.after li {
			font-size: var(--font-size-sm);
		}
	}

	/* Small mobile */
	@media (max-width: 480px) {
		.chapter-title {
			font-size: 1.5rem;
		}

		.inline-feature {
			flex-direction: column;
			text-align: center;
		}

		.feature-text h4 {
			font-size: var(--font-size-lg);
		}

		.feature-text p {
			font-size: var(--font-size-sm);
		}
	}

	.chapter.alternate {
		padding: var(--space-12);
		background: var(--bg-secondary);
		border-radius: var(--radius-xl);
		margin-inline: calc(-1 * var(--space-8));
	}

	@media (max-width: 768px) {
		.chapter.alternate {
			margin-inline: 0;
			padding: var(--space-8);
		}
	}
</style>
